import {
  createContext,
  useCallback,
  useState,
  useContext,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import { FounderId } from '../api/founders';
import { getGoal } from '../api/goals';
import { MentorId } from '../api/mentors';
import { Goal, GoalId, GoalStatus } from '../api/types/Goal';
import { Role } from '../api/user/Role';
import venturesAPI from '../api/ventures';
import { Venture } from '../api/ventures/types/Venture';
import { Attachment } from '../components/common/attachment-card';
import { UserContext } from './user-context';

export interface GoalContextProps {
  goal: Goal | null;
  ventures: Venture[];
  goalAttachments: Attachment[];
  isGoalLoading: boolean;
  isVenturesLoading: boolean;
  loadGoal: (goalId: GoalId) => Promise<void>;
  loadVentures: () => Promise<void>;
  setGoal: Dispatch<SetStateAction<Goal | null>>;
  setGoalAttachments: Dispatch<SetStateAction<Attachment[]>>;
  updateGoalProgressAndStatus: Function;
}

export const GoalContext = createContext<GoalContextProps>({
  goal: null,
  ventures: [],
  goalAttachments: [],
  isGoalLoading: false,
  isVenturesLoading: false,
  loadGoal: async () => {},
  loadVentures: async () => {},
  setGoal: () => {},
  setGoalAttachments: () => {},
  updateGoalProgressAndStatus: () => {},
});

interface GoalProviderProps {
  children: React.ReactNode;
}

const parseAttachments = (attachmentRefs: string): Attachment[] => {
  try {
    return JSON.parse(attachmentRefs);
  } catch (e: any) {
    return [];
  }
};

const getInitialAttachments = (goal: Goal | null) => {
  if (goal?.attachmentRefs) {
    return parseAttachments(goal?.attachmentRefs);
  }
  return [];
};

export const GoalProvider = ({ children }: GoalProviderProps) => {
  const [goal, setGoal] = useState<Goal | null>(null);
  const [ventures, setVentures] = useState<Venture[]>([]);
  const [isGoalLoading, setGoalLoading] = useState(true);
  const [isVenturesLoading, setVenturesLoading] = useState(true);
  const [goalAttachments, setGoalAttachments] = useState<Attachment[]>(
    getInitialAttachments(goal),
  );

  const { identityid, hasRole } = useContext(UserContext);

  const loadVentures = useCallback(async () => {
    if (identityid && (hasRole(Role.Mentor) || hasRole(Role.Founder))) {
      try {
        setVenturesLoading(true);
        let loadedVentures: Venture[] = [];

        if (hasRole(Role.Mentor)) {
          loadedVentures = await venturesAPI.getVenturesDetailsByMentorId(
            identityid as MentorId,
          );
        } else if (hasRole(Role.Founder)) {
          loadedVentures = await venturesAPI.getVenturesDetailsByFounderId(
            identityid as FounderId,
          );
        }

        setVentures(loadedVentures);
        setVenturesLoading(false);
      } catch (e: any) {
        setVenturesLoading(false);
        console.error('error loadVentures', e);
      }
    } else {
      // For admins/managers, don't load ventures
      setVentures([]);
      setVenturesLoading(false);
    }
  }, [identityid, hasRole]);

  const loadGoal = useCallback(
    async (goalId: GoalId) => {
      try {
        setGoalLoading(true);
        const loadedGoal = await getGoal(goalId);
        // For admins/managers, also fetch venture to hydrate ventureName for autocomplete
        if (hasRole(Role.Admin) || hasRole(Role.Manager)) {
          try {
            const venture = await venturesAPI.getVenture(loadedGoal.ventureId);
            const augmentedGoal = {
              ...(loadedGoal as Goal),
              ventureName: venture.ventureName,
            } as Goal & { ventureName?: string };
            setGoal(augmentedGoal);
            setGoalAttachments(getInitialAttachments(augmentedGoal));
          } catch (e) {
            setGoal(loadedGoal);
            setGoalAttachments(getInitialAttachments(loadedGoal));
          }
        } else {
          setGoal(loadedGoal);
          setGoalAttachments(getInitialAttachments(loadedGoal));
        }
        setGoalLoading(false);
      } catch (e: any) {
        setGoalLoading(false);
        console.error('error loadGoal', e);
      }
    },
    [hasRole],
  );

  const updateGoalProgressAndStatus = useCallback(
    ({ progress, status }: { progress: number; status: GoalStatus }) => {
      setGoal((prev) => (prev ? { ...prev, progress, status } : null));
    },
    [],
  );

  useEffect(() => {
    loadVentures();
  }, [loadVentures]);

  const contextValue: GoalContextProps = {
    goal,
    goalAttachments,
    ventures,
    isGoalLoading,
    isVenturesLoading,
    loadGoal,
    loadVentures,
    setGoal,
    setGoalAttachments,
    updateGoalProgressAndStatus,
  };

  return (
    <GoalContext.Provider value={contextValue}>{children}</GoalContext.Provider>
  );
};

export const useGoalContext = () => useContext(GoalContext);
